import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { useAuth } from '../../../hooks/use-auth'
import { useP2PCompletions } from '../../../hooks/use-p2pCompletions'
import { CompletionDistrictCard } from './CompletionDistrictCard'
import { styles } from './FormCompletions.styles'

// Form Completions: a per-building completion tracker grouped into per-district cards,
// grouped by ISD. No stat tiles — just the subtitle and the ISD-grouped district cards.
// Reminders are a per-district action on outstanding cards (ISD and RCN admins).
interface Props {
  readOnly?: boolean
}

export const FormCompletions = ({ readOnly = false }: Props): JSX.Element => {
  const { user } = useAuth()
  const { model, reportingYearId, isLoading } = useP2PCompletions()

  if (isLoading) return <CircularProgress />
  if (model == null) return <Typography variant='body1'>No completion data available.</Typography>

  const multipleIsds = model.isds.length > 1

  return (
    <Paper variant='outlined' sx={styles.page} data-testid='p2pFormCompletions'>
      <Typography variant='h3'>Form Completions</Typography>
      <Typography variant='body2' color='text.secondary' sx={styles.intro}>Track which buildings have submitted their Peer to Peer data below.</Typography>

      {model.isds.length === 0 && <Typography variant='body1'>No districts in your scope yet.</Typography>}

      <Stack gap='24px'>
        {model.isds.map(group => (
          <Box key={group.isdOrganizationId} data-testid={`p2pCompletionIsd-${group.isdOrganizationId}`}>
            {multipleIsds && <Typography variant='h4' sx={styles.isdHeading}>{group.isdName}</Typography>}
            <Box sx={styles.cardRow}>
              {group.districts.map(district => (
                <CompletionDistrictCard
                  key={district.districtOrganizationId}
                  district={district}
                  organizationId={user?.currentOrgId ?? 0}
                  reportingYearId={reportingYearId}
                  readOnly={readOnly}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </Paper>
  )
}
